import React, { FC, useState } from 'react';
import { useFormikContext } from 'formik';
import { useQuery } from '@apollo/react-hooks';
import {
  Checkbox,
  Button,
  Modal,
  Asset,
  ArrowRightAltIcon,
} from '@clearcover/atlas';
import { Link, useHistory } from 'react-router-dom';

import {
  FormActionFooter,
  Row,
  Column,
  FormInput,
  FormNavPrompt,
  HRule,
  InfoTooltip,
  ConfirmationModal,
  Flex,
} from 'Shared/components';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { Can } from 'Shared/utils/auth';
import { useAuth0 } from 'Auth';
import {
  createPartnerPolicy,
  updateAgencyCompliancePolicy,
  updatePartnerRole,
  viewPartnerRole,
  deletePartner,
  updateProtectedAttributes,
  viewPartnerPermission,
  updatePartnerPermission,
  canUpdatePartnerAddressPolicy,
} from 'Admin/authPolicies';
import { PartnerType, RateCallOneReports } from 'Admin/Partners';
import styles from './styles.module.css';
import { NpnStatus } from 'Admin/components/NpnStatus';
import { useLazyBaseApi } from 'Shared/services/Api';
import {
  NpnStatusType,
  NPN_STATUS_MAPPINGS,
} from 'Admin/components/NpnStatus/NpnStatus';
import { isMissing, padStartWith, stripLeadingZeros } from 'Shared/utils';
import { AgencyPdfSettings } from 'Admin/Partners/components/AgencyPdfSettings';
import { PolicyAccessLevel } from 'Admin/Partners/components/PolicyAccessLevel';
import { GetPartnerConfigType } from 'Admin/services/endpoints/GetPartnerConfig';
import { GET_PARTNER_CHANNEL_COMMISSION } from 'Admin/Partners/graphql';
import { NpnExternalLink } from 'Admin/components';
import { CopyContentButton } from 'Shared/components/CopyContentButton';

type GetPartnerChannelCommissionPayload = {
  channel: {
    enumValues: { name: string; description: string }[];
  };
  commissionStructure: {
    enumValues: { name: string; description: string }[];
  };
  sourceOfSale: {
    enumValues: { name: string; description: string }[];
  };
};

export type PartnerFormValues = {
  name: string;
  principalAgent: string;
  channel: string;
  commissionStructure: string;
  sourceOfSale: string;
  rateCallOneReports?: RateCallOneReports;
  licensedAgency: {
    employerIdentificationNumber: string;
    nationalProducerNumber: string;
    phoneNumber: string;
    soleProprietor: boolean;
  };
  address: {
    lineOne: string;
    lineTwo: string;
    city: string;
    zipCode: string;
    state: string;
  };
  canQuotePortal: boolean;
  npnStatus: NpnStatusType;
  quotePdfSettings?: {
    inheritParentInformation?: boolean;
    customizeContactInformation?: boolean;
    contactInformation?: {
      agencyName: string;
      agentName: string;
      emailAddress: string;
      phoneNumber: string;
    };
    readOnlySettings?: GetPartnerConfigType;
  };
  productOffering: {
    standardAuto: boolean;
    nonStandardAuto: boolean;
  };
  policyAccessLevel?: 'agency_scope' | 'hierarchy_scope';
};

export type PartnerFormPropsType = {
  type: 'create' | 'edit';
  footerError?: string;
  footerButtonText?: string;
  startQuotesInPortalRoleIsAssignable?: boolean;
  isPrincipalRoleAssignable?: boolean;
  isClearcoverUser?: boolean;
  currentPartner?: PartnerType;
  canEditRateCall?: boolean;
  canEditPdfSettings?: boolean;
};

function renderPartnerSequence(sequence: string | undefined) {
  const renderSequence = (seq?: string) =>
    seq ? 'COM' + padStartWith(seq, 5) : '';

  return (
    <div data-testid="partner-seq-number" className={styles.partnerSeqNumber}>
      Clearcover ID:
      <span data-testid="partner-seq-number-value">
        {renderSequence(sequence)}
      </span>
      <CopyContentButton value={renderSequence(sequence)} />
    </div>
  );
}

type ProductToggleRequest = {
  productType: 'standardAuto' | 'nonStandardAuto';
  currentValue: boolean;
};

const productTypeCopy = {
  standardAuto: 'Standard Auto',
  nonStandardAuto: 'NSA',
};

function renderProductChangeConfirmation({
  onCancel,
  onConfirm,
  productToggleRequest,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  productToggleRequest: ProductToggleRequest;
}) {
  return (
    <Modal open controlled showCloseIcon handleClose={onCancel}>
      {(close) => (
        <div className={styles.productConfirmation}>
          {!productToggleRequest.currentValue ? (
            <>
              <h3>
                Review and Confirm - Activate{' '}
                {productTypeCopy[productToggleRequest.productType]}
              </h3>
              <p>
                To activate a new product for this agency, please review and
                confirm the following.
              </p>
              <ul>
                <li>Relevant agency contracts have been fully executed</li>
                <li>
                  All licensed agents and sub-agencies will be authorized to
                  quote and sell the product
                </li>
              </ul>
            </>
          ) : (
            <>
              <h3>
                Review and Confirm - Deactivate{' '}
                {productTypeCopy[productToggleRequest.productType]}
              </h3>
              <p>Are you sure you want to deactivate this product?</p>
            </>
          )}
          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={close}
              data-testid="cancel-product-confirmation"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={onConfirm}
              data-testid="confirm-product-confirmation"
            >
              Confirm
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export const PartnerForm: FC<PartnerFormPropsType> = ({
  currentPartner,
  footerButtonText,
  footerError,
  startQuotesInPortalRoleIsAssignable,
  isPrincipalRoleAssignable = false,
  isClearcoverUser = false,
  type,
  canEditRateCall,
  canEditPdfSettings,
}) => {
  const { user } = useAuth0();
  const history = useHistory();

  const canEditAddress = canUpdatePartnerAddressPolicy(user!.effectiveGrants);

  const [productToggleRequest, setProductToggleRequest] =
    useState<ProductToggleRequest | null>(null);

  const { data: partnerChannelCommissionData } =
    useQuery<GetPartnerChannelCommissionPayload>(
      GET_PARTNER_CHANNEL_COMMISSION,
      {
        skip: !createPartnerPolicy(user!.effectiveGrants),
      }
    );

  const [
    { loading: postDeletePartnerLoading, error: postDeletePartnerError },
    postDeletePartner,
  ] = useLazyBaseApi<GetPartnerConfigType>({
    method: 'POST',
    url: `/agent_services/partner/${currentPartner?.id}/soft_delete`,
  });

  const {
    dirty,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    touched,
    values,
    status,
    setStatus,
    setTouched,
    initialStatus,
  } = useFormikContext<PartnerFormValues>();

  const partnerChannels =
    partnerChannelCommissionData?.channel?.enumValues.reduce(
      (acc, channel) => ({
        ...acc,
        [channel.name]: channel.description,
      }),
      {}
    );

  const partnerCommissionStructures =
    partnerChannelCommissionData?.commissionStructure?.enumValues.reduce(
      (acc, commissionStructure) => ({
        ...acc,
        [commissionStructure.name]: commissionStructure.description,
      }),
      {}
    );

  const partnerSourceOfSaleOptions =
    partnerChannelCommissionData?.sourceOfSale?.enumValues.reduce(
      (acc, sourceOfSale) => ({
        ...acc,
        [sourceOfSale.name]: sourceOfSale.description,
      }),
      {}
    );

  const reportsAtRC1Options = {
    ...(currentPartner?.ancestorIds.length
      ? { inherit: 'Inherit report configuration from parent agency' }
      : {}),
    ncf: 'Enable Credit-Based Insurance Score Report',
    none: "Don't run reports at Rate Call #1",
  };

  const canEditPartnerName =
    type === 'create' || // Can enter these values when in create mode
    (updateProtectedAttributes(user!.effectiveGrants) &&
      currentPartner &&
      !currentPartner.isCarrier);

  const canUpdatePartner = updateProtectedAttributes(user!.effectiveGrants);

  const canEditIdentityValues =
    type === 'create' || // Can enter these values when in create mode
    (currentPartner &&
      updateAgencyCompliancePolicy(user!.effectiveGrants, {
        ancestorIds: currentPartner.ancestorIds,
      }));

  const basePartnerRoleRequirements =
    currentPartner && startQuotesInPortalRoleIsAssignable;

  const canViewPartnerRole =
    basePartnerRoleRequirements &&
    viewPartnerRole(user!.effectiveGrants, currentPartner);

  const canUpdatePartnerRole =
    basePartnerRoleRequirements &&
    updatePartnerRole(user!.effectiveGrants, currentPartner);

  const isParentAgency =
    currentPartner?.isPrincipalAgency || currentPartner?.isCarrier;

  const canUpdateProductOffering =
    user &&
    isClearcoverUser &&
    updatePartnerPermission(user.effectiveGrants) &&
    isParentAgency;

  const canViewProductOffering =
    user &&
    isClearcoverUser &&
    viewPartnerPermission(user.effectiveGrants) &&
    !isParentAgency;

  const canDeletePartner =
    currentPartner &&
    !currentPartner.isCarrier &&
    deletePartner(user!.effectiveGrants);

  const matchesInitialValue =
    values.licensedAgency.nationalProducerNumber ===
    currentPartner?.licensedAgency?.nationalProducerNumber;

  const getNpnErrorMessage = () => {
    // NPN is not applicable if you are a sole proprietor
    if (!values.licensedAgency.soleProprietor) {
      // If you've touched the field and the field value is different, display yup validation
      if (
        touched.licensedAgency?.nationalProducerNumber &&
        !matchesInitialValue &&
        !isSubmitting
      ) {
        return errors.licensedAgency?.nationalProducerNumber;
      }
      // If the backend provided NPN status is "invalid", or you enter the initial value again for an NPN that was returned as invalid
      else if (
        status?.nationalProducerNumber ||
        (matchesInitialValue &&
          initialStatus.nationalProducerNumber === 'Invalid NPN')
      ) {
        return NPN_STATUS_MAPPINGS[values.npnStatus].message;
      }
    }
    return undefined;
  };

  const internalAdmin = user?.isCarrierUser || user?.isSystemUser;

  const solePropDisabled = !internalAdmin || !canEditIdentityValues;

  const npnValueIsEditable =
    isMissing(currentPartner?.licensedAgency?.nationalProducerNumber) || // No value yet
    values.npnStatus === 'invalid' || // value is invalid
    internalAdmin; // internal admins can edit regardless of status

  const npnDisabled =
    values.licensedAgency.soleProprietor || // Agency is a sole proprietorship
    !canEditIdentityValues || // Do not have permission
    !npnValueIsEditable;

  const einValueIsEditable =
    isMissing(currentPartner?.licensedAgency?.employerIdentificationNumber) || // No value yet
    internalAdmin; // internal admins can edit

  const einDisabled =
    !canEditIdentityValues || // Do not have permission
    values.licensedAgency.soleProprietor || // Agency is a sole proprietorship
    !einValueIsEditable;

  const npnErrorMessage = getNpnErrorMessage();
  const displayNpnStatus =
    matchesInitialValue &&
    !npnErrorMessage &&
    values.npnStatus !== 'invalid' &&
    !values.licensedAgency?.soleProprietor;

  const displayNPNExternalLink = !npnDisabled && values.npnStatus !== 'success';

  return (
    <form onSubmit={handleSubmit}>
      <FormNavPrompt when={dirty} />
      <Row>
        <Column className={styles.soleProprietorCheckboxWrapper}>
          <Checkbox
            label="Agency is a sole proprietor"
            name="licensedAgency.soleProprietor"
            value={values.licensedAgency.soleProprietor}
            onChange={(value) => {
              setFieldValue('licensedAgency.soleProprietor', value);
            }}
            className={styles.soleProprietorCheckbox}
            disabled={solePropDisabled}
          />
          <InfoTooltip
            size={16}
            tooltip="Check this box if this agency is a sole proprietor or individual producer and is not licensed under their own NPN."
          />
        </Column>
        <Column>{renderPartnerSequence(currentPartner?.seq)}</Column>
      </Row>
      <Row>
        <Column variant="half">
          <FormInput
            variant="text"
            name="name"
            label="Name"
            value={values.name}
            onChange={handleChange}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim();
              if (trimmedValue !== e.target.value) {
                setFieldValue('name', trimmedValue);
              }
              handleBlur(e);
            }}
            disabled={!canEditPartnerName}
            errorMessage={touched.name ? errors.name : undefined}
          />
        </Column>
        {currentPartner?.isCarrier ? (
          <Column />
        ) : (
          <Column variant="half" className={styles.principalAgentColumn}>
            <FormInput
              variant="text"
              name="principalAgent"
              label="Principal Agent"
              value={values.principalAgent}
              disabled={true}
            />
            {isMissing(currentPartner?.principalUser) &&
              isPrincipalRoleAssignable && (
                <div className={styles.principalAgentWarning}>
                  No principal agent assigned.{' '}
                  <Link
                    to={`/admin/partners/${currentPartner?.id}/users`}
                    className={styles.link}
                  >
                    Click here to assign a user{' '}
                    <Asset
                      className={styles.icon}
                      icon={ArrowRightAltIcon}
                      height={16}
                      width={16}
                      description="arrow"
                    />
                  </Link>
                </div>
              )}
          </Column>
        )}
      </Row>
      <Row>
        <Column variant="half">
          <FormInput
            variant="text"
            name="licensedAgency.nationalProducerNumber"
            label="Agency National Producer Number"
            tooltip={`Add agency NPN so licensed users in this agency can quote or complete premium-impacting endorsements. Once verified, this value can not be changed without calling a Clearcover advocate.`}
            value={
              values.licensedAgency.soleProprietor
                ? ''
                : values.licensedAgency.nationalProducerNumber
            }
            onChange={(e: React.FocusEvent<HTMLInputElement>) => {
              setStatus({
                ...status,
                nationalProducerNumber: undefined,
              });
              setTouched({
                ...touched,
                licensedAgency: {
                  ...(touched.licensedAgency || {}),
                  nationalProducerNumber: true,
                },
              });
              setFieldValue(
                'licensedAgency.nationalProducerNumber',
                stripLeadingZeros(e.target.value)
              );
            }}
            onBlur={handleBlur}
            disabled={npnDisabled}
            errorMessage={npnErrorMessage}
          />
          <div className={styles.npnPostLabels}>
            {displayNPNExternalLink && (
              <NpnExternalLink
                isAgency
                moveToTop={displayNpnStatus || !npnErrorMessage}
              />
            )}

            {displayNpnStatus && (
              <NpnStatus status={values.npnStatus}></NpnStatus>
            )}
          </div>
        </Column>
        <Column variant="half">
          <FormInput
            variant="text"
            name="licensedAgency.employerIdentificationNumber"
            label="Employer Identification Number"
            value={
              values.licensedAgency.soleProprietor
                ? ''
                : values.licensedAgency.employerIdentificationNumber
            }
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={einDisabled}
            errorMessage={
              touched.licensedAgency?.employerIdentificationNumber
                ? errors.licensedAgency?.employerIdentificationNumber
                : undefined
            }
          />
        </Column>
        <Column />
      </Row>
      <Can
        grants={user!.effectiveGrants}
        policy={createPartnerPolicy}
        yes={() => (
          <Row>
            <Column variant="third">
              <FormInput
                className={styles.partnerSelectOption}
                variant="select"
                name="channel"
                label="Partner Channel"
                value={values.channel}
                options={{ ...partnerChannels }}
                onChange={(value: string) => setFieldValue('channel', value)}
                onBlur={handleBlur}
                errorMessage={touched.channel ? errors.channel : undefined}
              />
            </Column>
            <Column variant="third">
              <FormInput
                className={styles.partnerSelectOption}
                variant="select"
                name="commissionStructure"
                label="Partner Commission Structure"
                value={values.commissionStructure}
                options={{ ...partnerCommissionStructures }}
                onChange={(value: string) =>
                  setFieldValue('commissionStructure', value)
                }
                onBlur={handleBlur}
                errorMessage={
                  touched.commissionStructure
                    ? errors.commissionStructure
                    : undefined
                }
              />
            </Column>
            <Column variant="third">
              <FormInput
                variant="select"
                name="sourceOfSale"
                label="Source of Sale"
                value={values.sourceOfSale}
                options={{ ...partnerSourceOfSaleOptions }}
                onChange={(value: string) =>
                  setFieldValue('sourceOfSale', value)
                }
                onBlur={handleBlur}
                errorMessage={
                  touched.sourceOfSale ? errors.sourceOfSale : undefined
                }
                disabled={!canUpdatePartner}
              />
            </Column>
          </Row>
        )}
        no={() => null}
      />
      <HRule size="lg" />
      <h3>Contact Information</h3>
      <Row>
        <Column>
          <Row>
            <Column variant="half">
              <FormInput
                variant="text"
                name="address.lineOne"
                label="Address"
                value={values.address.lineOne}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={!canEditAddress}
              />
            </Column>
            <Column variant="half">
              <FormInput
                variant="text"
                name="address.lineTwo"
                label="Address 2"
                value={values.address.lineTwo}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={!canEditAddress}
              />
            </Column>
          </Row>
        </Column>
        <Column variant="sixth" />
      </Row>
      <Row>
        <Column>
          <Row>
            <Column variant="half">
              <FormInput
                variant="text"
                name="address.city"
                label="City"
                value={values.address.city}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled={!canEditAddress}
              />
            </Column>
            <Column variant="half">
              <FormInput
                variant="selectAutocomplete"
                name="address.state"
                label="State"
                value={values.address.state}
                options={STATE_OPTIONS}
                onChange={(value: string) =>
                  setFieldValue('address.state', value)
                }
                disabled={!canEditAddress}
              />
            </Column>
          </Row>
        </Column>
        <Column variant="sixth">
          <FormInput
            variant="text"
            name="address.zipCode"
            label="Zip Code"
            value={values.address.zipCode}
            onBlur={handleBlur}
            onChange={handleChange}
            errorMessage={
              touched.address?.zipCode ? errors.address?.zipCode : undefined
            }
            disabled={!canEditAddress}
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Row>
            <Column variant="half">
              <FormInput
                variant="text"
                name="licensedAgency.phoneNumber"
                label="Phone"
                value={values.licensedAgency.phoneNumber}
                onBlur={handleBlur}
                onChange={handleChange}
                errorMessage={
                  touched.licensedAgency?.phoneNumber
                    ? errors.licensedAgency?.phoneNumber
                    : undefined
                }
              />
            </Column>
            <Column />
          </Row>
        </Column>
        <Column variant="sixth" />
      </Row>
      {(canViewProductOffering || canUpdateProductOffering) &&
        isClearcoverUser && (
          <>
            <HRule size="lg" />
            <h3>Product Offering</h3>
            <Flex className={styles.productsWrapper} wrap="wrap">
              <Column variant="third">
                <div className={styles.productItem}>
                  <Checkbox
                    key="productOffering.standardAuto"
                    label="Standard Auto"
                    name="productOffering.standardAuto"
                    value={values.productOffering.standardAuto}
                    toggle
                    disabled={!canUpdateProductOffering}
                    hideLabel
                    onChange={() => {
                      setProductToggleRequest({
                        productType: 'standardAuto',
                        currentValue: values.productOffering.standardAuto,
                      });
                    }}
                  />
                  <div className={styles.productItemInfo}>
                    <span>Standard Auto</span>
                    <p>Clearcover Insurance Company</p>
                  </div>
                </div>
              </Column>
              <Column variant="third">
                <div className={styles.productItem}>
                  <Checkbox
                    key="productOffering.nonStandardAuto"
                    label="Non-Standard Auto"
                    name="productOffering.nonStandardAuto"
                    value={values.productOffering.nonStandardAuto}
                    toggle
                    disabled={!canUpdateProductOffering}
                    hideLabel
                    onChange={() => {
                      setProductToggleRequest({
                        productType: 'nonStandardAuto',
                        currentValue: values.productOffering.nonStandardAuto,
                      });
                    }}
                  />
                  <div className={styles.productItemInfo}>
                    <span>Non-Standard Auto</span>
                    <p>Clearcover InterInsurance Exchange</p>
                  </div>
                </div>
              </Column>
            </Flex>
          </>
        )}
      {canViewPartnerRole && (
        <div data-testid="update-grants">
          <HRule size="lg" />
          <div className={styles.authorizedToQuoteWrapper}>
            <h3>Authorized to Quote</h3>
            <InfoTooltip
              tooltip="This setting also applies to all sub-agencies of this partner."
              size={16}
            />
          </div>

          <Checkbox
            key="canQuotePortal"
            label="Partner can quote from portal"
            name="canQuotePortal"
            value={values.canQuotePortal}
            disabled={!canUpdatePartnerRole}
            onChange={() =>
              setFieldValue('canQuotePortal', !values.canQuotePortal)
            }
          />
        </div>
      )}
      {canEditRateCall && (
        <>
          <HRule size="lg" />
          <h3>Reports run at Rate Call #1</h3>
          <Row>
            <Column variant="half">
              <FormInput
                variant="select"
                name="rateCallOneReports"
                label="Select which reports should run during Rate Call #1"
                value={values.rateCallOneReports}
                options={reportsAtRC1Options}
                disabled={currentPartner?.name === 'clearcover'}
                onChange={(value: string) => {
                  setFieldValue('rateCallOneReports', value);
                }}
              />
            </Column>
          </Row>
        </>
      )}

      {canEditPdfSettings && (
        <AgencyPdfSettings ancestorIds={currentPartner?.ancestorIds} />
      )}

      {canEditPdfSettings && (
        <PolicyAccessLevel ancestorIds={currentPartner?.ancestorIds} />
      )}

      {canDeletePartner && (
        <>
          <HRule size="lg" />
          <Modal
            toggle={(openModal) => (
              <Button
                type="button"
                variant="danger-secondary"
                isSubmitting={postDeletePartnerLoading}
                data-testid="partner-delete"
                onClick={openModal}
              >
                Delete Agency
              </Button>
            )}
          >
            {(close) => (
              <ConfirmationModal
                onSubmit={async () => {
                  await postDeletePartner();

                  const parentId =
                    currentPartner?.ancestorIds[
                      currentPartner?.ancestorIds.length - 1
                    ];
                  history.replace(
                    parentId
                      ? `/admin/partners/${parentId}/agencies`
                      : // fallback scenario if carriers are ever allowed to be deleted
                        '/admin/all-partners'
                  );
                }}
                error={postDeletePartnerError?.message}
                close={close}
                isSubmitting={postDeletePartnerLoading}
                headerText="Are you sure you want to delete this agency?"
                bodyText={`You are about to delete ${currentPartner?.name}. This cannot be undone.`}
                backText="Cancel"
                confirmText="Delete Agency"
              />
            )}
          </Modal>
        </>
      )}

      {productToggleRequest &&
        renderProductChangeConfirmation({
          productToggleRequest,
          onCancel: () => setProductToggleRequest(null),
          onConfirm: () => {
            setFieldValue(
              `productOffering.${productToggleRequest.productType}`,
              !productToggleRequest.currentValue
            );
            setProductToggleRequest(null);
          },
        })}

      <FormActionFooter
        error={footerError}
        disabled={!dirty}
        isSubmitting={isSubmitting}
        buttonText={footerButtonText}
      />
    </form>
  );
};
